import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Loader2, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { ROOM_TYPES } from '../../../lib/roomTypes';
import { BudgetSummary } from './BudgetConfirmDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROOM_STYLES = [
  { id: 'modern_minimalist', label: 'Modern Minimalist' },
  { id: 'scandinavian', label: 'Scandinavian' },
  { id: 'contemporary', label: 'Contemporary' },
  { id: 'traditional', label: 'Traditional' },
  { id: 'industrial', label: 'Industrial' },
  { id: 'coastal', label: 'Coastal' },
  { id: 'mid_century', label: 'Mid-Century Modern' },
  { id: 'farmhouse', label: 'Farmhouse' },
];

export const GenerateRoomsDialog = ({ collection, token, open, onOpenChange, onStarted }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  // Per-room selection + style
  const [roomSelections, setRoomSelections] = useState(() => {
    const init = {};
    ROOM_TYPES.forEach(r => { init[r.id] = { selected: true, style: 'modern_minimalist' }; });
    return init;
  });

  useEffect(() => {
    if (!open || !collection) return;
    setSummary(null);
    setError(null);
    setLoading(true);
    // Reset selections
    const init = {};
    ROOM_TYPES.forEach(r => { init[r.id] = { selected: true, style: 'modern_minimalist' }; });
    setRoomSelections(init);

    axios.get(`${API_URL}/admin/fabrics/collections/${collection.id}/scenes/missing`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setSummary(data))
      .catch((err) => setError(err.response?.data?.detail || 'Could not load scene status'))
      .finally(() => setLoading(false));
  }, [open, collection, token]);

  const toggleRoom = (roomId) => {
    setRoomSelections(prev => ({
      ...prev,
      [roomId]: { ...prev[roomId], selected: !prev[roomId].selected }
    }));
  };

  const setRoomStyle = (roomId, style) => {
    setRoomSelections(prev => ({
      ...prev,
      [roomId]: { ...prev[roomId], style }
    }));
  };

  const selectedRoomCount = Object.values(roomSelections).filter(r => r.selected).length;

  const start = async () => {
    setStarting(true);
    try {
      // Pass room selections with styles
      const rooms = Object.entries(roomSelections)
        .filter(([_, v]) => v.selected)
        .map(([id, v]) => ({ room_type: id, style: v.style }));

      const { data } = await axios.post(
        `${API_URL}/admin/fabrics/collections/${collection.id}/scenes/generate-missing`,
        { room_selections: rooms },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Generating ${data.total} room scenes in the background`);
      onStarted?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start generation');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="generate-rooms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-600" /> Generate Room Scenes
          </DialogTitle>
          <DialogDescription>
            For <strong>{collection?.name}</strong>. Select which rooms to generate and their interior style.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>}
        {error && <p className="text-sm text-red-600" data-testid="generate-rooms-error">{error}</p>}
        {summary && (
          <div className="space-y-3">
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-neutral-50 p-2.5">
                <p className="text-xl font-semibold">{summary.color_count}</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Colors</p>
              </div>
              <div className="rounded-lg bg-green-50 p-2.5">
                <p className="text-xl font-semibold text-green-700">{summary.existing_count}</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">Existing</p>
              </div>
              <div className="rounded-lg bg-amber-50 p-2.5">
                <p className="text-xl font-semibold text-amber-700" data-testid="generate-rooms-missing-count">{summary.missing_count}</p>
                <p className="text-[10px] uppercase tracking-wider text-neutral-500">To Generate</p>
              </div>
            </div>

            {/* Per-room selection with style dropdown */}
            <div className="border rounded-lg divide-y max-h-[280px] overflow-y-auto">
              {ROOM_TYPES.map((room) => {
                const missing = summary.per_room?.[room.id] ?? 0;
                const sel = roomSelections[room.id] || { selected: true, style: 'modern_minimalist' };
                const isComplete = missing === 0;

                return (
                  <div key={room.id} className={`px-3 py-2.5 ${isComplete ? 'bg-green-50/50' : ''}`} data-testid={`room-row-${room.id}`}>
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={sel.selected && !isComplete}
                          disabled={isComplete}
                          onChange={() => toggleRoom(room.id)}
                          className="accent-amber-600 w-4 h-4"
                          data-testid={`room-check-${room.id}`}
                        />
                        <span className={`text-sm font-medium ${isComplete ? 'text-green-700' : 'text-neutral-800'}`}>
                          {room.label}
                        </span>
                      </label>

                      {/* Status or style selector */}
                      {isComplete ? (
                        <span className="text-green-700 text-xs flex items-center gap-1 flex-shrink-0">
                          <Check className="w-3.5 h-3.5" /> complete
                        </span>
                      ) : (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-neutral-400">{missing} missing</span>
                          <select
                            value={sel.style}
                            onChange={(e) => setRoomStyle(room.id, e.target.value)}
                            disabled={!sel.selected}
                            className="text-xs border border-neutral-200 rounded-md px-2 py-1 bg-white disabled:opacity-40 max-w-[140px]"
                            data-testid={`room-style-${room.id}`}
                          >
                            {ROOM_STYLES.map(s => (
                              <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <BudgetSummary estimate={summary.estimate} testId="generate-rooms-budget" />
            <p className="text-xs text-neutral-500">Select specific rooms and styles. Only missing scenes will be generated.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="generate-rooms-cancel">Cancel</Button>
          <Button
            onClick={start}
            disabled={!summary || summary.missing_count === 0 || starting || selectedRoomCount === 0}
            className="bg-amber-600 hover:bg-amber-700 gap-2"
            data-testid="generate-rooms-confirm"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {summary?.missing_count === 0
              ? 'Nothing to generate'
              : `Generate ${selectedRoomCount} room types · ~$${summary?.estimate?.estimated_cost_usd?.toFixed(2) ?? '…'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateRoomsDialog;
